import * as THREE from 'three';
import { liveLabelGap } from '../runNeuralAnimationFrame/labelSlots';
import { camUp, diskR, nestDepthOf, right, titleWorld, viewAlong } from './camera';

/**
 * Pixel size of a name tag, read from layout once and remembered on the
 * label record. `offsetWidth` forces a reflow when styles were just written,
 * and the solver asks for every nested name several times a frame. The size
 * only changes when the text changes or the tag mounts / unmounts.
 */
export function labelPxSize(rec, el) {
  if (!el) return null;
  const mounted = !!rec?.label?.parent;
  const text = el.textContent;
  if (rec && rec.pxW > 0 && rec.pxText === text && rec.pxMounted === mounted) return rec;
  const w = el.offsetWidth || 80;
  const h = el.offsetHeight || 16;
  if (!rec) return { pxW: w, pxH: h };
  rec.pxW = w;
  rec.pxH = h;
  rec.pxText = text;
  rec.pxMounted = mounted;
  return rec;
}

export function hangBoxAt(camera, pos, mesh, rec, cw, ch, halfHAt1) {
  const el = rec?.element;
  if (!el) return null;
  const hang = diskR(mesh) + liveLabelGap(mesh, camera, ch);
  const lx = pos.x - camUp.x * hang;
  const ly = pos.y - camUp.y * hang;
  const lz = pos.z - camUp.z * hang;
  const tz = viewAlong({ x: lx, y: ly, z: lz }, camera);
  if (tz < 0.3) return null;
  const cx = camera.position.x;
  const cy = camera.position.y;
  const cz = camera.position.z;
  const tx = (lx - cx) * right.x + (ly - cy) * right.y + (lz - cz) * right.z;
  const ty = (lx - cx) * camUp.x + (ly - cy) * camUp.y + (lz - cz) * camUp.z;
  const halfH = tz * halfHAt1;
  const halfW = halfH * camera.aspect;
  const px = labelPxSize(rec, el);
  const bw = (px.pxW / cw) * 2 * halfW;
  const bh = (px.pxH / ch) * 2 * halfH;
  return {
    tz,
    left: tx - bw * 0.5,
    right: tx + bw * 0.5,
    bottom: ty - bh,
    top: ty,
  };
}

export function collectLabelOccluders(labelByTaskId, camera, container, meshByTaskId) {
  const out = [];
  if (!labelByTaskId || !container) return out;
  const cw = Math.max(container.clientWidth, 1);
  const ch = Math.max(container.clientHeight, 1);
  const halfHAt1 = Math.tan(THREE.MathUtils.degToRad(camera.fov) * 0.5);
  labelByTaskId.forEach((rec, tid) => {
    if (!rec.label?.parent) return;
    const el = rec.element;
    if (!el || parseFloat(el.style.opacity) < 0.08) return;
    rec.label.getWorldPosition(titleWorld);
    const tz = viewAlong(titleWorld, camera);
    if (tz < 0.3) return;
    const tx = (titleWorld.x - camera.position.x) * right.x
      + (titleWorld.y - camera.position.y) * right.y
      + (titleWorld.z - camera.position.z) * right.z;
    const ty = (titleWorld.x - camera.position.x) * camUp.x
      + (titleWorld.y - camera.position.y) * camUp.y
      + (titleWorld.z - camera.position.z) * camUp.z;
    const halfH = tz * halfHAt1;
    const halfW = halfH * camera.aspect;
    const px = labelPxSize(rec, el);
    const bw = (px.pxW / cw) * 2 * halfW;
    const bh = (px.pxH / ch) * 2 * halfH;
    const cx = rec.label.center?.x ?? 0.5;
    const cy = rec.label.center?.y ?? 0;
    out.push({
      tid,
      depth: nestDepthOf(meshByTaskId?.get(tid)),
      tz,
      left: tx - bw * cx,
      right: tx + bw * (1 - cx),
      bottom: ty - bh * (1 - cy),
      top: ty + bh * cy,
    });
  });
  return out;
}

export function collectTitleOccluders(nebula, camera, container) {
  const out = [];
  if (!nebula?.visible || !container) return out;
  const cw = Math.max(container.clientWidth, 1);
  const ch = Math.max(container.clientHeight, 1);
  const halfHAt1 = Math.tan(THREE.MathUtils.degToRad(camera.fov) * 0.5);
  nebula.children.forEach((bunch) => {
    const tag = bunch.userData?.tag;
    const el = tag?.element;
    if (!tag || !el || el.dataset.editing === '1') return;
    if (el.style.visibility === 'hidden' || parseFloat(el.style.opacity) < 0.08) return;
    tag.getWorldPosition(titleWorld);
    const tz = viewAlong(titleWorld, camera);
    if (tz < 0.3) return;
    const tx = (titleWorld.x - camera.position.x) * right.x
      + (titleWorld.y - camera.position.y) * right.y
      + (titleWorld.z - camera.position.z) * right.z;
    const ty = (titleWorld.x - camera.position.x) * camUp.x
      + (titleWorld.y - camera.position.y) * camUp.y
      + (titleWorld.z - camera.position.z) * camUp.z;
    const halfH = tz * halfHAt1;
    const halfW = halfH * camera.aspect;
    const bw = ((el.offsetWidth || 80) / cw) * 2 * halfW;
    const bh = ((el.offsetHeight || 16) / ch) * 2 * halfH;
    out.push({
      tz,
      left: tx - bw * 0.5,
      right: tx + bw * 0.5,
      bottom: ty,
      top: ty + bh,
    });
  });
  return out;
}

export function ensureHome(ud, mesh) {
  if (!ud.homePos) ud.homePos = (ud.targetPos ? ud.targetPos.clone() : mesh.position.clone());
  return ud.homePos;
}

export function livePos(mesh, tid, targets) {
  const home = ensureHome(mesh.userData, mesh);
  const rev = targets.get(tid) || { x: mesh.userData.revealX ?? 0, y: mesh.userData.revealY ?? 0 };
  return { x: home.x + rev.x, y: home.y + rev.y, z: home.z };
}
